import React from "react";
import { type ColDef, themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import Button from "src/components/Button/Button";
import Loading from "src/components/Loading";

import { formatPercent, percentChange } from "./metrics.utils";

import styles from "./CompanyMetricsTab.module.scss";

interface IKpiProps {
  label: string;
  value: string;
  current?: number;
  previous?: number;
  hint?: string;
}

export const KpiCard: React.FC<IKpiProps> = ({ label, value, current, previous, hint }) => {
  const change = current !== undefined && previous !== undefined ? percentChange(current, previous) : undefined;

  return (
    <div className={styles.kpi}>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>{value}</div>
      {change !== undefined ? (
        <div className={change >= 0 ? `${styles.kpiChange} ${styles.up}` : `${styles.kpiChange} ${styles.down}`}>
          {change >= 0 ? "▲" : "▼"} {formatPercent(Math.abs(change))} vs previous period
        </div>
      ) : (
        hint && <div className={styles.kpiChange}>{hint}</div>
      )}
    </div>
  );
};

export const KpiRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.kpiRow}>{children}</div>
);

interface IChartCardProps {
  title: string;
  subtitle?: string;
  onExport?(): void;
  wide?: boolean;
  children: React.ReactNode;
}

export const ChartCard: React.FC<IChartCardProps> = ({ title, subtitle, onExport, wide, children }) => (
  <div className={wide ? `${styles.card} ${styles.wide}` : styles.card}>
    <div className={styles.cardHeader}>
      <div>
        <div className={styles.cardTitle}>{title}</div>
        {subtitle && <div className={styles.cardSubtitle}>{subtitle}</div>}
      </div>
      {onExport && (
        <Button className={styles.exportButton} onClick={onExport}>
          Export CSV
        </Button>
      )}
    </div>
    <div className={styles.cardBody}>{children}</div>
  </div>
);

export const EmptyState: React.FC<{ message?: string }> = ({ message }) => (
  <div className={styles.empty}>{message || "No data for this period."}</div>
);

const gridTheme = themeQuartz.withParams({
  fontFamily: "Roboto",
  headerFontFamily: "Roboto",
  cellFontFamily: "Roboto",
});

interface IMetricsGridProps<T> {
  rows: T[];
  columns: ColDef<T>[];
  pageSize?: number;
}

export const MetricsGrid = <T,>({ rows, columns, pageSize }: IMetricsGridProps<T>) => {
  if (!rows.length) return <EmptyState />;
  const paginate = !!pageSize && rows.length > pageSize;

  return (
    <div className={styles.gridTable}>
      <AgGridReact<T>
        theme={gridTheme}
        rowData={rows}
        columnDefs={columns}
        defaultColDef={{ sortable: true, resizable: true, flex: 1, minWidth: 110 }}
        domLayout="autoHeight"
        pagination={paginate}
        paginationPageSize={pageSize}
        paginationPageSizeSelector={false}
        suppressCellFocus
      />
    </div>
  );
};

export const LoadingState: React.FC = () => (
  <div className={styles.loading}>
    <Loading fullScreen={false} size="small" message="Loading metrics…" />
  </div>
);
